import {
  IconFolder,
  IconLayoutDashboard,
  IconPackages,
  IconPalette,
  IconSettings,
  IconUsersGroup,
  IconUsers,
  IconAccessPoint,
  IconCreditCard,
  IconFolderSearch,
} from '@tabler/icons-react'
import { tokens } from '@/locales/translations/tokens'
import {
  AudioWaveform,
  BookOpenText,
  Bot,
  BrainCog,
  BriefcaseBusiness,
  ChartPie,
  Command,
  FileCog,
  Folders,
  GalleryVerticalEnd,
  Headset,
  Key,
  Megaphone,
  SparklesIcon,
  TextSearch,
  BookMarked,
  Languages,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { type SidebarDataWithLoading } from '../../types'
import { getFeatureFlags } from '@/api/feature-flags'

export const useSidebarData = (): SidebarDataWithLoading => {
  const { t } = useTranslation()
  const { data, isPending } = useQuery({
    queryKey: ['featureFlags', 'share', 'ui'],
    queryFn: async () => {
      const response = await getFeatureFlags({ type: 'ui' })
      const uiSideMenuFlag = response.ui_flags?.find(flag => flag.key === 'UI_SIDE_MENU_RENDERING')
      if (!uiSideMenuFlag) return null
      const flagKey = uiSideMenuFlag.key
      const value = uiSideMenuFlag.value
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null
      }
      const record = value as Record<string, unknown>
      const nested = record[flagKey]
      let menu: FeatureFlags | null = null
      if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        const inner = nested as Record<string, unknown>
        const sampleKey = Object.keys(inner)[0]
        const sample = sampleKey ? inner[sampleKey] : undefined
        if (
          sample &&
          typeof sample === 'object' &&
          !Array.isArray(sample) &&
          ('features' in sample || 'plans' in sample)
        ) {
          menu = inner as FeatureFlags
        }
      }
      if (!menu) {
        menu = value as FeatureFlags
      }
      return menu && typeof menu === 'object' ? menu : null
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 5 * 60 * 1000,
  })
  const featureFlags = data ?? null
  const isLoading = isPending

  const getFeatureData = (key: string) => {
    if (!featureFlags) return { features: null, plans: null }
    return featureFlags[key] || { features: null, plans: null }
  }

  return {
    isLoading,
    user: {
      name: 'satnaing',
      email: 'satnaingdev@gmail.com',
      avatar: '/avatars/shadcn.jpg',
    },
    teams: [
      {
        name: 'Shadcn Admin',
        logo: Command,
        plan: 'Vite + ShadcnUI',
      },
      {
        name: 'Acme Inc',
        logo: GalleryVerticalEnd,
        plan: 'Enterprise',
      },
      {
        name: 'Acme Corp.',
        logo: AudioWaveform,
        plan: 'Startup',
      },
    ],
    navGroups: [
      {
        title: t(tokens.sidebar.section.general),
        items: [
          {
            title: 'Workstation',
            icon: BriefcaseBusiness,
            ...getFeatureData('workstation'),
            items: [
              {
                title: t(tokens.ui.actions.ask),
                url: '/',
                icon: SparklesIcon,
              },
              {
                title: t(tokens.workstation.memory.title),
                url: '/?memory=true',
                icon: BrainCog,
              },
              {
                title: t(tokens.workstation.history.title),
                url: '/?history=true',
                icon: TextSearch,
              },
            ],
          },
          {
            title: t(tokens.sidebar.translation.title),
            icon: Languages,
            ...getFeatureData('translator'),
            items: [
              {
                title: t(tokens.sidebar.translation.translation),
                url: '/translation-translation',
                icon: IconFolder,
              },
              {
                title: t(tokens.sidebar.translation.fileManage),
                url: '/translation-file-manage',
                icon: FileCog,
              },
              {
                title: t(tokens.sidebar.translation.glossary),
                url: '/translation-glossary',
                icon: BookMarked,
              },
            ],
          },
          {
            title: t(tokens.aihub.gatewayTitle),
            url: '/ai-hub',
            icon: Bot,
            ...getFeatureData('ai-hub')
          },
          {
            title: t(tokens.sidebar.files.title),
            icon: Folders,
            ...getFeatureData('file-manager'),
            items: [
              {
                title: t(tokens.sidebar.files.myFolders),
                url: '/file-manager',
                icon: IconFolder,
                ...getFeatureData('my-folders')
              },
              {
                title: t(tokens.sidebar.files.search),
                url: '/file-search',
                icon: IconFolderSearch,
                ...getFeatureData('search-folders')
              }
            ],
          },
          {
            title: t(tokens.sidebar.dashboard),
            url: '/dashboard',
            icon: IconLayoutDashboard,
            ...getFeatureData('dashboard')
          },
          {
            title: t(tokens.sidebar.integrations),
            url: '/integrations',
            icon: IconPackages,
            ...getFeatureData('integrations')
          },
        ],
      },
      {
        title: t(tokens.sidebar.section.other),
        items: [
          {
            title: t(tokens.sidebar.settings),
            icon: IconSettings,
            items: [
              {
                title: t(tokens.sidebar.usage),
                url: '/settings/usage',
                icon: ChartPie
              },
              {
                title: t(tokens.sidebar.billing),
                url: '/settings/billing',
                icon: IconCreditCard,
                ...getFeatureData('billing')
              },
              {
                title: t(tokens.sidebar.members),
                url: '/settings/members',
                icon: IconUsers,
              },
              {
                title: t(tokens.sidebar.groups),
                url: '/settings/groups',
                icon: IconUsersGroup,
              },
              {
                title: t(tokens.settings.appearance.preferences.title),
                url: '/settings/preferences',
                icon: IconPalette,
              },
              {
                title: t(tokens.settings.apiKey.title),
                url: '/settings/tokens',
                icon: Key,
              },
            ],
          },
          {
            title: 'Release Notes',
            url: '#release-notes',
            icon: Megaphone
          },
          {
            title: t(tokens.tickets.chat.support_label),
            url: '/tickets',
            icon: Headset
          },
          {
            title: t(tokens.sidebar.statusPage),
            url: 'https://status.mrturing.ai/',
            icon: IconAccessPoint,
            redirectionMethod: '_blank'
          },
          {
            title: t(tokens.sidebar.documentation),
            url: 'https://docs.mrturing.ai/',
            icon: BookOpenText,
            redirectionMethod: '_blank'
          },
        ],
      },
    ],
  }
}
